
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  getClassificationScores,
  getNormalScores,
  getBalancedEquationScores,
  getPrinciplesScores,
  courseMembersQuery
} from '../api';

import { navigation } from '../data';
import { popupMessagesDecisionInsightsOverall, popupMessagesDecisionInsightsNoGamesData } from './data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';

import './styles.css';

export default function InstructorInsightsOverall() {
  const navigate = useNavigate();

  const [filterBy, setFilterBy] = useState('Accuracy');
  const [data, setData] = useState([]);

  const [popupMessages, setPopupMessages] = useState([]);

  const filterByOptions = ['Accuracy', 'Points'];

  const { data: courseMembers } = useQuery(courseMembersQuery()) || {};

  const { data: games } = useQuery(gamesQuery()) || {};

  const { data: classificationScores } = useQuery({
    queryKey: ['classificationScoresOverall'],
    queryFn: async () => getClassificationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: normalScores } = useQuery({
    queryKey: ['normalScoresOverall'],
    queryFn: async () => getNormalScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: balancedEquationScores } = useQuery({
    queryKey: ['equationScoresOverall'],
    queryFn: async () => getBalancedEquationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: principlesScores } = useQuery({
    queryKey: ['principlesScoresOverall'],
    queryFn: async () => getPrinciplesScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if(classificationScores && normalScores && balancedEquationScores && principlesScores && courseMembers?.length > 0 && games) {
      formatLeaderboardData();
      const allScores = [...classificationScores, ...normalScores, ...balancedEquationScores, ...principlesScores];
      const instructorIds = new Set(
        courseMembers
          .filter(member => member.is_instructor)
          .map(member => member.user_id)
      );
      const uniqueUsers = new Set(allScores.map(score => score.user));
      const uniqueUserCount = Array.from(uniqueUsers).filter(userId => !instructorIds.has(userId)).length;

      const studentCount = courseMembers.filter(member => !member.is_instructor).length;

      if(games.length > 0 && uniqueUserCount > 0) {
        const updatedMessages = popupMessagesDecisionInsightsOverall.map(message => ({
          ...message,
          text: message.text.replace('{allGamesUsersCount}', uniqueUserCount)
            .replace('{courseMembersCount}', studentCount || 0)
        }));
        setPopupMessages(updatedMessages);
      } else {
        setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
      }
    }
  }, [classificationScores, normalScores, balancedEquationScores, principlesScores, courseMembers, filterBy, games]);

  const calculateAverage = (data, param) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record[param];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };

  const formatLeaderboardData = () => {
    const instructorIds = new Set(
      courseMembers
        .filter(member => member.is_instructor)
        .map(member => member.user_id)
    );

    const filteredClassificationScores = classificationScores.filter(score => !instructorIds.has(score.user));
    const filteredNormalScores = normalScores.filter(score => !instructorIds.has(score.user));
    const filteredBalancedEquationScores = balancedEquationScores.filter(score => !instructorIds.has(score.user));
    const filteredPrinciplesScores = principlesScores.filter(score => !instructorIds.has(score.user));

    const classificationScore = filterBy === 'Accuracy' ? calculateAverage(filteredClassificationScores, 'total_accuracy') : calculateAverage(filteredClassificationScores, 'total_points');
    const normalScore = filterBy === 'Accuracy' ? calculateAverage(filteredNormalScores, 'total_accuracy') : calculateAverage(filteredNormalScores, 'total_points');  
    const balancedEquationScore = filterBy === 'Accuracy' ? calculateAverage(filteredBalancedEquationScores, 'total_accuracy') : calculateAverage(filteredBalancedEquationScores, 'total_points');
    const principlesScore = filterBy === 'Accuracy' ? calculateAverage(filteredPrinciplesScores, 'total_accuracy') : calculateAverage(filteredPrinciplesScores, 'total_points');

    const sectionData = [
      ...(classificationScore ? [{ title: "Classification", score: classificationScore * 100 || 0, color: '#ECF6F7' }] : []),
      ...(normalScore ? [{ title: "Normal Balance", score: normalScore * 100 || 0, color: '#FFF8EB' }] : []),
      ...(balancedEquationScore ? [{ title: "Double Entry", score: balancedEquationScore * 100 || 0, color: '#EAE4F9' }] : []),
      ...(principlesScore ? [{ title: "The Principles", score: principlesScore * 100 || 0, color: '#C7DBFF' }] : [])
    ];

    const data = [
      ...(sectionData.length > 0 ? [{
        title: 'Class Average Accuracy',
        bars: sectionData,
        barsLabel: '%'
      }] : []),
    ];

    setData(data);
  }


  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/leaderboard/average-time');
  }

  const handleNextButtonClick = () => {
    let redirectUrl = '/instructor/insights/progress-scores/progress';
    if(games && games.length > 0) {
      if(games.some(game => game.game_type === 'Account Classification' || game.game_type === 'Normal Balance')) {
        redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
      } else if(games.some(game => game.game_type === 'Balanced Equation')) {
        redirectUrl = '/instructor/insights/decision-insights/double-entry';
      } else if(games.some(game => game.game_type === 'The Principles')) {
        redirectUrl = '/instructor/insights/decision-insights/principles';
      }
    }
    navigate(redirectUrl);
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.map(bar => bar.score) || []
  ));

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={1}
      pagesQuantity={4}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>Insights | Overall</h1>
      <div className="decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores 
          data={data}
          size="small"
          maxScore={maxScore}
          alignBarTitle="center"
        />
      </div>
    </SubPagesLayout>
  );
}
